import React, { useState } from 'react';
import { ListItem, ListItemText, Avatar, Box, Menu, MenuItem, ListItemIcon } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTheme } from '@mui/material/styles';
import {
  getBaseListItemStyles,
  listItemTextStyles,
  listItemTextTypographyProps,
  shouldShowText,
} from './navigationStyles';

/**
 * Extracts initials from a name or email
 * @param {string} name - User's name
 * @param {string} email - User's email
 * @returns {string} One or two character initials
 */
const getInitials = (name, email) => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  return '?';
};

/**
 * UserAvatarItem component for displaying user avatar in the sidebar
 *
 * @param {Object} props - Component props
 * @param {Object} props.user - User object with name and email
 * @param {boolean} props.miniDrawer - Whether the drawer is in mini mode
 * @param {boolean} props.isDesktop - Whether the current view is desktop
 * @param {Function} props.onLogout - Function to handle logout
 * @returns {JSX.Element} UserAvatarItem component
 */
const UserAvatarItem = ({ user, miniDrawer, isDesktop, onLogout }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const initials = getInitials(user?.name, user?.email);
  const hasName = Boolean(user?.name);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    onLogout?.();
  };

  return (
    <>
      <ListItem
        onClick={handleClick}
        sx={{
          ...getBaseListItemStyles(theme),
          gap: '0.375rem',
          width: 'unset',
          height: 'unset',
          margin: '0 -0.25rem -0.25rem -0.25rem',
          paddingTop: '0.5rem',
          paddingBottom: '0.5rem',
          cursor: 'pointer',
          ...(hasName && {
            height: 'auto',
            py: 1,
          }),
          ...(miniDrawer && isDesktop && {
            justifyContent: 'center',
            p: 0,
          }),
          '&:hover': {
            bgcolor: 'var(--sidebar-item-hover-bg)',
          },
        }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: '0.875rem',
            fontWeight: 600,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        >
          {initials}
        </Avatar>
        {shouldShowText(isDesktop, miniDrawer) && (
          <>
            <ListItemText
              sx={listItemTextStyles}
              primary={hasName ? user.name : user?.email || 'User'}
              secondary={hasName ? user?.email : undefined}
              primaryTypographyProps={{
                ...listItemTextTypographyProps,
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ...(!hasName && { fontSize: '0.75rem' }),
              }}
              secondaryTypographyProps={{
                fontSize: '0.75rem',
                color: theme.palette.text.muted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            />
            <Box
              component="svg"
              sx={{
                width: 16,
                height: 16,
                flexShrink: 0,
                ml: 'auto',
                color: theme.palette.text.muted,
              }}
            >
              <use href="/icons.svg#icon-selector" />
            </Box>
          </>
        )}
      </ListItem>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 160,
              bgcolor: 'var(--bg-panel)',
              backdropFilter: 'var(--effect-panel-backdrop-filter)',
              border: 'var(--border-width) solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
            },
          },
        }}
      >
        <MenuItem onClick={handleLogout} sx={{ fontSize: 'var(--sidebar-item-font-size)' }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserAvatarItem;
